import { MessageBox, MessageBoxButton, Mobile } from '@bimser/common';
import { BCDataGrid, BCExtremeGridEntities, findMessage } from '@bimser/components';
import { MLHelper } from '@bimser/core';
import dayjs from 'dayjs';
import _isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { IDataSourceResponse } from '../../../../../entities/common';
import { IDataGridRowCollection } from '../../DataGrid/entities';
import Styles from '../assets/styles.scss';
import { ILookupGridProps } from '../entities/ILookupGridProps';

const LookupGrid = (props: ILookupGridProps) => {
	const bcDataGridRef = React.useRef<BCDataGrid.Entities.BCDataGridRef>();

	React.useEffect(() => {
		Mobile.MobileView.setFunctionForMobile(Mobile.MobileFunctions.CloseLookupModal, () => {
			MessageBox.resolveModal({
				id: props.messageBoxId,
				button: {
					id: MessageBoxButton.OK,
					text: findMessage.get('100004')
				}
			});
			MessageBox.closeModal(props.messageBoxId);
			props.onClose?.();
		});

		bcDataGridRef.current?.instance().selectRows(props.selectedValues, true);
	}, []);

	const getUniqueDataField = () =>
		props.gridOptions?.columns?.find(i => i.isPrimaryKey)?.name || null;

	const onSelectionChanged = React.useCallback(e => {
		if (props.onRowSelect) {
			props.onRowSelect(e.selectedRowsData || []);
		}
	}, []);

	const onRowDblClick = args => {
		if (props.onRowSelect) {
			props.onRowSelect(args.data ? [args.data] : null);
		}
		if (props.messageBoxId) {
			MessageBox.resolveModal({
				id: props.messageBoxId,
				button: {
					id: MessageBoxButton.OK,
					text: findMessage.get('100004')
				}
			});
			MessageBox.closeModal(props.messageBoxId);
		}
		props.onRowDoubleClick?.(args.data);
	};

	const handleRowsForLookup = (rows: IDataGridRowCollection) =>
		rows.map(row => {
			const rowData = {};
			if (!_isEmpty(row.data)) {
				return row.data;
			} else {
				row.cells.forEach(cell => {
					rowData[cell.name] = cell.value || null;
				});
			}
			return { ...rowData };
		});

	const getColumnDataType = type => {
		switch (type) {
			case 'boolean':
				return 'boolean';
			case 'decimal':
			case 'number':
			case 'int':
				return 'number';
			case 'date':
			case 'dateTime':
			case 'time':
				return 'datetime';
			default:
				return 'string';
		}
	};

	const getColumnDbType = column => {
		switch (column.dbType.type) {
			case 'boolean':
				return BCExtremeGridEntities.ItemType.Boolean;
			case 'decimal':
			case 'number':
			case 'int':
				return BCExtremeGridEntities.ItemType.Decimal;
			case 'date':
			case 'dateTime':
			case 'time':
				return BCExtremeGridEntities.ItemType.DateTimeOffset;
			default:
				return BCExtremeGridEntities.ItemType.String;
		}
	};

	const getColumns = React.useMemo((): BCDataGrid.Entities.IRemoteColumnProps[] => {
		let controlAllowHiding = false;
		return (
			props.gridOptions.columns.map((column, index) => {
				const dataType = getColumnDataType(column?.dbType?.type);
				let allowHiding = column.visible;
				let allowFiltering: boolean;
				let allowSorting: boolean;
				let format;
				let dataField = column.name;
				let filterOperations: string[];
				let cellComponent;
				let allowSearch = true;

				const imageDisplayCellTemplate = args =>
					BCDataGrid.Components.ImageDisplayCell({
						value: args.data.value,
						enlargeOnClick: column.enlargeOnClick,
						imageWidth: column.imageWidth,
						defaultImage: column.defaultImage,
						sizeUnit: column.sizeUnit,
						className: Styles.imageCellTemplate,
						sourceType: column.sourceType
					});

				if (!controlAllowHiding && allowHiding) {
					allowHiding = false;
					controlAllowHiding = true;
				}

				if (column?.editType == 'actionButton' || column?.formatType?.type == 'image') {
					allowFiltering = false;
					allowSorting = false;
					allowSearch = false;
				}

				if (dataType == 'datetime') {
					format = {
						formatter(value) {
							const date = dayjs(value);

							if (date.isValid()) {
								return date.format(column.format || 'L LT');
							}

							return value;
						}
					};
				} else if (dataType == 'number') {
					filterOperations =
						BCDataGrid.Helpers.DefaultFilterOperations.NumericFilterOperations;

					let _format = '';

					if (column.useThousandSeparator) {
						_format = _format.concat(',###');
					}

					if (column.precision) {
						let localeParser = '';

						for (let index = 0; index < column.precision; index++) {
							localeParser = localeParser.concat('#');
						}

						_format = _format.concat(`.${localeParser}`);
					}

					if (_format.length) {
						format = _format;
					}
				}

				if (column?.dbType?.type == 'object' && column.editType == 'multiLanguageTextBox') {
					dataField = `${column.name}.${MLHelper.currentLanguage.culture}`;
				}

				if (dataType == 'boolean') {
					cellComponent = BCDataGrid.Components.checkBoxDisplayCell;
				} else if (column?.formatType?.type == 'image') {
					cellComponent = imageDisplayCellTemplate;
				}

				return {
					name: column.name,
					dataField,
					caption: MLHelper.getMLText(column.caption),
					alignment: column.align,
					width: column.width,
					dataType,
					visibleIndex: index,
					visible: column.visible,
					allowHiding: allowHiding,
					allowFiltering,
					allowSorting,
					selectedFilterOperation: dataType !== 'string' ? '=' : 'contains',
					filterOperations,
					dbType: getColumnDbType(column),
					allowSearch,
					format,
					cellComponent
				};
			}) || []
		);
	}, [props.gridOptions.columns]);

	const store = React.useMemo(
		() =>
			new BCDataGrid.Components.CustomStore({
				key: getUniqueDataField(),
				async load(loadOptions) {
					try {
						const _loadOptions = BCDataGrid.Helpers.convertLoadOptions(
							loadOptions,
							getColumns
						);

						let ignoreMobile = false;

						if (
							Object.prototype.hasOwnProperty.call(loadOptions, 'select') &&
							typeof loadOptions.select === 'undefined'
						) {
							ignoreMobile = true;
							_loadOptions.pagination.take = 9999;
						}

						if (_loadOptions.filters.length) {
							ignoreMobile = true;
						}

						const response: IDataSourceResponse = await props.getRows(
							_loadOptions,
							ignoreMobile
						);

						if (!response.success) {
							console.log(response);
						}

						return {
							data: response?.result || [],
							totalCount: response?.count || 0
						};
					} catch {
						throw new Error('Data Loading Error');
					}
				}
			}),
		[]
	);

	const getDataSource = () => {
		if (props.gridOptions.columns.length == 0) {
			Mobile.MobileView.setFormLookupRows({
				controlId: props.name,
				rows: [],
				count: 0,
				pageIndex: 0
			});
			return [];
		}

		if (props.dataSource?.runAtServer) {
			return props.rows?.length ? handleRowsForLookup(props.rows) : [];
		} else {
			return store;
		}
	};

	const height = calculateHeight(props.height);

	return (
		<BCDataGrid.Components.DataGrid
			name={BCDataGrid.Entities.DataGrids.LookupGrid}
			ref={bcDataGridRef}
			dataSource={getDataSource()}
			keyExpr={getUniqueDataField() || 'id'}
			remoteOperations={!props.dataSource?.runAtServer}
			cacheEnabled
			height={height}
			width="100%"
			cellHintEnabled
			showColumnLines={false}
			showRowLines
			columnMinWidth={50}
			columnAutoWidth={false}
			allowColumnResizing={props.gridOptions.columnSettings.resizeable}
			allowColumnReordering={props.gridOptions.columnSettings.orderable}
			onRowDblClick={onRowDblClick}
			defaultSelectedRowKeys={props.selectedValues}
			onSelectionChanged={onSelectionChanged}
			customizeColumns={
				!props.dataSource?.runAtServer ? undefined : BCDataGrid.Constants.customizeColumns
			}
		>
			<BCDataGrid.Components.Paging
				enabled
				defaultPageSize={props.gridOptions.paging.pageSize || 10}
				defaultPageIndex={props.gridOptions.paging.currentPage - 1}
			/>
			<BCDataGrid.Components.Pager
				allowedPageSizes={props.gridOptions.paging.pagingSizes}
				showPageSizeSelector={props.gridOptions.paging.enabled}
				showNavigationButtons={props.gridOptions.paging.enabled}
				showInfo
				visible={props.gridOptions.paging.enabled}
			/>
			<BCDataGrid.Components.FilterRow
				key="filterRow"
				visible={props.gridOptions.filtering.enabled}
				showOperationChooser={props.gridOptions.filtering.enabled}
			/>
			<BCDataGrid.Components.SearchPanel
				visible={props.gridOptions.searching.enabled}
				highlightCaseSensitive
			/>
			<BCDataGrid.Components.Sorting mode={props.gridOptions.sorting.mode} />
			<BCDataGrid.Components.Selection
				mode={props.gridOptions.selection.mode || 'single'}
				showCheckBoxesMode="onClick"
				selectAllMode="page"
			/>
			<BCDataGrid.Components.ColumnChooser
				mode="select"
				allowSearch={false}
				enabled={props.gridOptions.columnSettings.showColumnChooser}
			/>
			{getColumns.map(column => (
				<BCDataGrid.Components.Column
					key={column.dataField}
					{...column}
				/>
			))}
		</BCDataGrid.Components.DataGrid>
	);
};

export default LookupGrid;

const calculateHeight = (height: unknown) => {
	if (typeof height === 'string') {
		height = Number(height);
	}

	if (!Number.isNaN(height) && typeof height === 'number') {
		return Math.floor(height / 10) * 10;
	}

	return '100%';
};
